import React, { useState, useEffect, useRef } from "react";
import {
    Upload,
    FileText,
    Search,
    MoreHorizontal,
    Trash2,
    Eye,
    ChevronDown,
    Plus,
    X,
    MessageSquare,
    Type,
    Globe,
    Loader2,
} from "lucide-react";
import LearnMoreModal from "../components/ui/LearnMoreModal";
import UpgradeModal from "../components/ui/UpgradeModal";
import apiClient from "../services/apiClient";
import API_CONFIG from "../config/api";
import { useSubscription } from "../hooks/useSubscription";
import { usePlanFeatures } from "../hooks/usePlanFeatures";
import { InlineLoader, SkeletonLoader } from "../components/ui/Loader";

const SourcesPage = () => {
    const [selectedCategory, setSelectedCategory] = useState("files");
    const [dragActive, setDragActive] = useState(false);
    const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [activeAccordion, setActiveAccordion] = useState("files");
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const { hasActiveSubscription, canAccessFeature } = useSubscription();
    const { features } = usePlanFeatures();
    const maxCharactersTotal = features?.maxCharactersTotal || 10000; // Default to Basic plan limit

    // Q&A form state
    const [qaForm, setQaForm] = useState({ question: "", answer: "" });

    // Text form state
    const [textForm, setTextForm] = useState({ title: "", content: "" });

    // Website form state
    const [websiteForm, setWebsiteForm] = useState({ url: "" });
    const [scraping, setScraping] = useState(false);
    const [scrapedData, setScrapedData] = useState(null);
    const [userWebsite, setUserWebsite] = useState("");

    // Forms are now handled via database, no local state needed

    // File sources state
    const [sources, setSources] = useState([]);
    const [isLoadingSources, setIsLoadingSources] = useState(true);
    const [stats, setStats] = useState({
        file_count: 0,
        qa_count: 0,
        text_count: 0,
        total_chars: 0,
    });

    // Search and sort state
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("newest");
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

    // File selection state - using array instead of Set for simplicity
    const [selectedFileIds, setSelectedFileIds] = useState([]);

    // Toast state
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });

    // Modal state
    const [modal, setModal] = useState({ show: false, type: null, data: null });

    // Dropdown ref for click outside
    const sortDropdownRef = useRef(null);
    const fileInputRef = useRef(null);

    // Load sources from API
    const loadSources = async () => {
        try {
            setIsLoadingSources(true);
            const response = await apiClient.get("/api/sources");

            if (response.success) {
                setSources(response.data.sources);
                setStats(response.data.stats);
            }
        } catch (error) {
            showToast("Failed to load sources", "error");
        } finally {
            setIsLoadingSources(false);
        }
    };

    // Load sources and user website on component mount
    useEffect(() => {
        loadSources();
        const loadUserWebsite = async () => {
            try {
                const response = await apiClient.get("/api/general");
                if (response.success && response.data.settings?.websiteUrl) {
                    const url = response.data.settings.websiteUrl;
                    if (url && url !== "https://") {
                        setUserWebsite(url);
                        setWebsiteForm({ url });
                    }
                }
            } catch {}
        };
        loadUserWebsite();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
                setSortDropdownOpen(false);
            }
        };

        if (sortDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [sortDropdownOpen]);

    // Clear selections when search or sort changes (disabled for debugging)
    // useEffect(() => {
    //   setSelectedFiles(new Set());
    // }, [searchQuery, sortBy]);

    // Calculate character counts from real database data (with safety checks)
    const filesCharCount = (sources || []).filter((s) => s.type === "file").reduce((total, source) => total + (source.char_count || 0), 0);
    const textCharCount = (sources || []).filter((s) => s.type === "text").reduce((total, source) => total + (source.char_count || 0), 0);
    const qaCharCount = (sources || []).filter((s) => s.type === "qa").reduce((total, source) => total + (source.char_count || 0), 0);
    const totalCharCount = stats.total_chars || filesCharCount + textCharCount + qaCharCount;

    // Filter and sort sources by type
    const filteredAndSortedSources = (sources || [])
        .filter((source) => source.type === "file") // Only show files in the file sources section
        .filter((source) => (source.title || source.original_filename || "").toLowerCase().includes(searchQuery.toLowerCase()))
        .sort((a, b) => {
            switch (sortBy) {
                case "newest":
                    return new Date(b.created_at) - new Date(a.created_at);
                case "oldest":
                    return new Date(a.created_at) - new Date(b.created_at);
                case "name":
                    return (a.title || a.original_filename || "").localeCompare(b.title || b.original_filename || "");
                case "size":
                    return (b.char_count || 0) - (a.char_count || 0);
                default:
                    return 0;
            }
        });

    // Get Q&A and text sources from database
    const qaSourcesFromDB = (sources || []).filter((source) => source.type === "qa").sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const textSourcesFromDB = (sources || [])
        .filter((source) => source.type === "text")
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const websiteSourcesFromDB = (sources || []).filter((source) => source.type === "text" && source.metadata?.fromWebsite);
    const websiteCharCount = websiteSourcesFromDB.reduce((total, source) => total + (source.char_count || 0), 0);

    const categories = [
        { id: "files", label: "Files", count: stats.file_count || 0, chars: `${filesCharCount.toLocaleString()} chars` },
        { id: "text", label: "Text", count: stats.text_count || 0, chars: `${textCharCount.toLocaleString()} chars` },
        { id: "website", label: "Website", count: websiteSourcesFromDB.length, chars: `${websiteCharCount.toLocaleString()} chars` },
        { id: "qa", label: "Q&A", count: stats.qa_count || 0, chars: `${qaCharCount.toLocaleString()} chars` },
    ];

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (!canAccessFeature("sources")) {
            setShowUpgradeModal(true);
            return;
        }

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFiles(e.dataTransfer.files);
        }
    };

    const removeFile = async (fileId) => {
        try {
            const response = await apiClient.delete(`/api/sources/${fileId}`);

            if (response.success) {
                // Remove from local state
                setSources((prev) => prev.filter((source) => source.id !== fileId));
                // Remove from selection if it was selected
                setSelectedFileIds((prev) => prev.filter((id) => id !== fileId));

                // Reload sources to get updated stats
                await loadSources();

                showToast("Source removed successfully", "success");
            } else {
                throw new Error(response.message || "Failed to delete source");
            }
        } catch (error) {
            showToast("Failed to remove source. Please try again.", "error");
        }
    };

    const removeUploadingFile = (fileName) => {
        setUploadingFiles((prev) => prev.filter((file) => file.name !== fileName));
    };

    // Toast utilities
    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3000);
    };

    // Modal utilities
    const openModal = (type, data) => {
        setModal({ show: true, type, data });
    };

    const closeModal = () => {
        setModal({ show: false, type: null, data: null });
    };

    // File validation
    const validateFile = (file) => {
        const allowedTypes = ["pdf", "doc", "docx", "txt", "md"];
        const fileExtension = file.name.split(".").pop().toLowerCase();
        const maxSize = 10 * 1024 * 1024; // 10MB

        if (!allowedTypes.includes(fileExtension)) {
            showToast(`File type .${fileExtension} is not supported. Please upload PDF, DOC, DOCX, TXT, or MD files.`, "error");
            return false;
        }

        if (file.size > maxSize) {
            showToast("File size must be less than 10MB.", "error");
            return false;
        }

        return true;
    };

    // Process files (both drag & drop and click upload)
    const processFiles = (files) => {
        const validFiles = Array.from(files).filter(validateFile);

        if (validFiles.length === 0) return;

        validFiles.forEach((file) => {
            // Add to uploading files with real file object
            setUploadingFiles((prev) => [
                ...prev,
                {
                    name: file.name,
                    progress: 0,
                    status: "uploading",
                    file: file,
                },
            ]);

            // Start real file upload
            uploadFile(file);
        });
    };

    // Real file upload function
    const uploadFile = async (file) => {
        try {
            // Create FormData for file upload
            const formData = new FormData();
            formData.append("file", file);

            // Simulate upload progress
            let progress = 0;
            const progressInterval = setInterval(() => {
                progress += Math.random() * 15 + 5; // 5-20% increments

                setUploadingFiles((prev) =>
                    prev.map((f) =>
                        f.name === file.name
                            ? {
                                  ...f,
                                  progress: Math.min(progress, 95),
                                  status: progress < 95 ? "uploading" : "processing",
                              }
                            : f
                    )
                );

                if (progress >= 95) {
                    clearInterval(progressInterval);
                }
            }, 300);

            // Make actual API call to backend
            const response = await apiClient.post("/api/sources/file", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            clearInterval(progressInterval);

            if (response.success) {
                // Remove from uploading files
                setUploadingFiles((prev) => prev.filter((f) => f.name !== file.name));

                // Reload sources from server to get updated data
                await loadSources();

                showToast(`File "${file.name}" uploaded successfully!`, "success");
            } else {
                throw new Error(response.message || "Upload failed");
            }
        } catch (error) {
            showToast(`Failed to upload "${file.name}". Please try again.`, "error");
            setUploadingFiles((prev) => prev.filter((f) => f.name !== file.name));
        }
    };

    // Q&A form handlers
    const handleQaSubmit = async () => {
        if (!canAccessFeature("sources")) {
            setShowUpgradeModal(true);
            return;
        }

        if (qaForm.question.trim() && qaForm.answer.trim()) {
            try {
                const response = await apiClient.post("/api/sources/qa", {
                    question: qaForm.question.trim(),
                    answer: qaForm.answer.trim(),
                });

                if (response.success) {
                    // Clear form and close accordion
                    setQaForm({ question: "", answer: "" });
                    setActiveAccordion(null);

                    // Reload sources to get updated data
                    await loadSources();

                    showToast("Q&A added successfully!", "success");
                } else {
                    throw new Error(response.message || "Failed to create Q&A");
                }
            } catch (error) {
                showToast("Failed to add Q&A. Please try again.", "error");
            }
        } else {
            showToast("Please fill in both question and answer fields.", "error");
        }
    };

    const handleQaCancel = () => {
        setQaForm({ question: "", answer: "" });
        setActiveAccordion(null);
    };

    // Text form handlers
    const handleTextSubmit = async () => {
        if (!canAccessFeature("sources")) {
            setShowUpgradeModal(true);
            return;
        }

        if (textForm.content.trim()) {
            try {
                const response = await apiClient.post("/api/sources/text", {
                    title: textForm.title.trim() || "Untitled",
                    content: textForm.content.trim(),
                });

                if (response.success) {
                    // Clear form and close accordion
                    setTextForm({ title: "", content: "" });
                    setActiveAccordion(null);

                    // Reload sources to get updated data
                    await loadSources();

                    showToast("Text content added successfully!", "success");
                } else {
                    throw new Error(response.message || "Failed to create text");
                }
            } catch (error) {
                showToast("Failed to add text content. Please try again.", "error");
            }
        } else {
            showToast("Please enter some text content.", "error");
        }
    };

    const handleTextCancel = () => {
        setTextForm({ title: "", content: "" });
        setActiveAccordion(null);
    };

    // Website scrape handlers
    const handleWebsiteScrape = async () => {
        if (!websiteForm.url.trim()) {
            showToast("Please enter a URL.", "error");
            return;
        }

        try {
            setScraping(true);
            setScrapedData(null);

            const response = await apiClient.post("/api/sources/scrape", {
                url: websiteForm.url.trim(),
            });

            if (response.success) {
                setScrapedData(response.data);
            } else {
                throw new Error(response.message || "Failed to scrape website");
            }
        } catch (error) {
            showToast(error.message || "Failed to scrape website. Check the URL and try again.", "error");
        } finally {
            setScraping(false);
        }
    };

    const handleWebsiteSave = async () => {
        if (!scrapedData) return;

        if (!canAccessFeature("sources")) {
            setShowUpgradeModal(true);
            return;
        }

        try {
            const response = await apiClient.post("/api/sources/text", {
                title: scrapedData.title || websiteForm.url,
                content: scrapedData.content,
            });

            if (response.success) {
                setWebsiteForm({ url: "" });
                setScrapedData(null);
                setActiveAccordion(null);
                await loadSources();
                showToast("Website content added successfully!", "success");
            } else {
                throw new Error(response.message || "Failed to save website content");
            }
        } catch (error) {
            showToast("Failed to save website content. Please try again.", "error");
        }
    };

    const handleWebsiteCancel = () => {
        setWebsiteForm({ url: "" });
        setScrapedData(null);
        setActiveAccordion(null);
    };

    // File input handlers
    const handleFileInputChange = (e) => {
        if (!canAccessFeature("sources")) {
            setShowUpgradeModal(true);
            e.target.value = ""; // Reset input
            return;
        }

        if (e.target.files && e.target.files.length > 0) {
            processFiles(e.target.files);
            // Reset input to allow same file to be selected again
            e.target.value = "";
        }
    };

    const triggerFileInput = () => {
        if (!canAccessFeature("sources")) {
            setShowUpgradeModal(true);
            return;
        }
        fileInputRef.current?.click();
    };

    // Simple selection helpers
    const isFileSelected = (fileId) => selectedFileIds.includes(fileId);
    const visibleFileIds = (filteredAndSortedSources || []).map((source) => source.id);
    const selectedCount = selectedFileIds.length;
    const visibleCount = visibleFileIds.length;
    const allVisibleSelected = visibleCount > 0 && visibleFileIds.every((id) => selectedFileIds.includes(id));
    const someVisibleSelected = visibleFileIds.some((id) => selectedFileIds.includes(id));

    // File selection handlers - completely new logic
    const toggleSelectAll = () => {
        if (allVisibleSelected) {
            // If all visible files are selected, deselect all visible files
            setSelectedFileIds((prev) => prev.filter((id) => !visibleFileIds.includes(id)));
        } else {
            // If not all visible files are selected, select all visible files
            const newSelected = [...selectedFileIds];
            visibleFileIds.forEach((id) => {
                if (!newSelected.includes(id)) {
                    newSelected.push(id);
                }
            });
            setSelectedFileIds(newSelected);
        }
    };

    const toggleFileSelection = (fileId) => {
        setSelectedFileIds((prev) => {
            if (prev.includes(fileId)) {
                return prev.filter((id) => id !== fileId);
            } else {
                return [...prev, fileId];
            }
        });
    };

    const deleteSelectedFiles = async () => {
        if (selectedFileIds.length === 0) return;

        try {
            const response = await apiClient.post("/api/sources/delete-multiple", {
                sourceIds: selectedFileIds,
            });

            if (response.success) {
                // Clear selection
                setSelectedFileIds([]);

                // Reload sources to get updated data
                await loadSources();

                showToast(`Deleted ${response.data.deletedCount} source${response.data.deletedCount === 1 ? "" : "s"}`, "success");
            } else {
                throw new Error(response.message || "Failed to delete sources");
            }
        } catch (error) {
            showToast("Failed to delete sources. Please try again.", "error");
        }
    };

    return (
        <div className="flex flex-col lg:flex-row">
            {/* Main Content */}
            <div className="flex-1 lg:order-1">
                {/* Header */}
                <div className="container-mobile py-4 sm:py-6 md:py-8 pb-4 sm:pb-5 md:pb-6">
                    <div className="mb-4 sm:mb-6">
                        <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-gray-900 dark:text-white mb-2">Files</h1>
                        <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500 font-light">Upload documents to train your AI. Extract text from PDFs, DOC and TXT files.</p>
                        <button
                            onClick={() => setIsLearnMoreOpen(true)}
                            className="text-xs sm:text-sm text-blue-500 hover:text-blue-600 inline-flex items-center gap-1 mt-2 transition-colors duration-200"
                        >
                            <span>Learn more</span>
                        </button>
                    </div>

                    {/* Accordion Sections */}
                    <div className="mb-6 sm:mb-8 space-y-3 sm:space-y-4">
                        {/* Add Files Accordion */}
                        <div className="border border-gray-200 dark:border-white/[0.06] rounded-[16px] overflow-hidden">
                            <button
                                onClick={() => setActiveAccordion(activeAccordion === "files" ? null : "files")}
                                className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors duration-200"
                            >
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Add files</h2>
                                </div>
                                <ChevronDown
                                    className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ${
                                        activeAccordion === "files" ? "transform rotate-180" : ""
                                    }`}
                                />
                            </button>

                            <div
                                className={`transition-all duration-300 ease-in-out ${
                                    activeAccordion === "files" ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                                }`}
                            >
                                <div className="p-3 sm:p-4 border-t border-gray-100 dark:border-white/[0.06]">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept=".pdf,.doc,.docx,.txt,.md"
                                        onChange={handleFileInputChange}
                                        className="hidden"
                                    />
                                    <div
                                        onClick={triggerFileInput}
                                        className={`border-2 border-dashed rounded-[12px] p-6 sm:p-8 text-center cursor-pointer transition-colors duration-200 ${
                                            dragActive
                                                ? "border-blue-400 bg-blue-500/[0.05] dark:bg-blue-500/[0.05]"
                                                : "border-gray-200 dark:border-white/[0.08] hover:border-blue-400 hover:bg-blue-500/[0.05] dark:hover:bg-blue-500/[0.05]"
                                        }`}
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                                            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500">
                                                <span className="font-medium">Drag & drop files here or click to select files</span>
                                            </p>
                                            <p className="text-xs sm:text-sm text-gray-400 dark:text-gray-600">Supported file types: PDF, DOC, DOCX, TXT, MD (max 10MB)</p>
                                        </div>
                                    </div>

                                    {/* Uploading Files */}
                                    {uploadingFiles.length > 0 && (
                                        <div className="mt-4 sm:mt-6 space-y-2 sm:space-y-3">
                                            {uploadingFiles.map((file) => (
                                                <div
                                                    key={file.name}
                                                    className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 dark:bg-white/[0.03] rounded-[12px]"
                                                >
                                                    <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</span>
                                                            <button
                                                                onClick={() => removeUploadingFile(file.name)}
                                                                className="text-gray-400 hover:text-gray-600 btn-touch p-1"
                                                            >
                                                                <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                            </button>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-1 bg-gray-200 dark:bg-white/[0.08] rounded-full h-1.5 sm:h-2">
                                                                <div
                                                                    className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                                                                        file.status === "uploading" ? "bg-blue-500" : "bg-yellow-500"
                                                                    }`}
                                                                    style={{ width: `${file.progress}%` }}
                                                                ></div>
                                                            </div>
                                                            <span className="text-xs text-gray-500 dark:text-gray-500 capitalize whitespace-nowrap">{file.status}...</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Add Q&A Accordion */}
                        <div className="border border-gray-200 dark:border-white/[0.06] rounded-[16px] overflow-hidden">
                            <button
                                onClick={() => setActiveAccordion(activeAccordion === "qa" ? null : "qa")}
                                className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors duration-200"
                            >
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Add Q&A</h2>
                                </div>
                                <ChevronDown
                                    className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ${
                                        activeAccordion === "qa" ? "transform rotate-180" : ""
                                    }`}
                                />
                            </button>

                            <div
                                className={`transition-all duration-300 ease-in-out ${
                                    activeAccordion === "qa" ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                                }`}
                            >
                                <div className="p-3 sm:p-4 border-t border-gray-100 dark:border-white/[0.06]">
                                    <div className="space-y-3 sm:space-y-4">
                                        <div>
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Question</label>
                                            <textarea
                                                value={qaForm.question}
                                                onChange={(e) => setQaForm((prev) => ({ ...prev, question: e.target.value }))}
                                                className="w-full px-3 py-2 rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-xs sm:text-sm resize-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/50"
                                                rows="3"
                                                placeholder="Enter the question your customers might ask..."
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Answer</label>
                                            <textarea
                                                value={qaForm.answer}
                                                onChange={(e) => setQaForm((prev) => ({ ...prev, answer: e.target.value }))}
                                                className="w-full px-3 py-2 rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-xs sm:text-sm resize-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/50"
                                                rows="4"
                                                placeholder="Enter the answer your AI should provide..."
                                            />
                                        </div>
                                        <div className="flex flex-col-reverse xs:flex-row justify-end gap-2 sm:gap-3">
                                            <button
                                                onClick={handleQaCancel}
                                                className="w-full xs:w-auto px-4 py-2 text-xs sm:text-sm bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors duration-200"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleQaSubmit}
                                                className="w-full xs:w-auto px-4 py-2 text-xs sm:text-sm bg-black dark:bg-white text-white dark:text-black rounded-full hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors duration-200"
                                            >
                                                Add Q&A
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Add Text Accordion */}
                        <div className="border border-gray-200 dark:border-white/[0.06] rounded-[16px] overflow-hidden">
                            <button
                                onClick={() => setActiveAccordion(activeAccordion === "text" ? null : "text")}
                                className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors duration-200"
                            >
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <Type className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Add text</h2>
                                </div>
                                <ChevronDown
                                    className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ${
                                        activeAccordion === "text" ? "transform rotate-180" : ""
                                    }`}
                                />
                            </button>

                            <div
                                className={`transition-all duration-300 ease-in-out ${
                                    activeAccordion === "text" ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                                }`}
                            >
                                <div className="p-3 sm:p-4 border-t border-gray-100 dark:border-white/[0.06]">
                                    <div className="space-y-3 sm:space-y-4">
                                        <div>
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title (optional)</label>
                                            <input
                                                type="text"
                                                value={textForm.title}
                                                onChange={(e) => setTextForm((prev) => ({ ...prev, title: e.target.value }))}
                                                className="w-full px-3 py-2 rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-xs sm:text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/50"
                                                placeholder="Enter a title for this text content..."
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Content</label>
                                            <textarea
                                                value={textForm.content}
                                                onChange={(e) => setTextForm((prev) => ({ ...prev, content: e.target.value }))}
                                                className="w-full px-3 py-2 rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-xs sm:text-sm resize-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/50"
                                                rows="6"
                                                placeholder="Enter your text content here. This could be policies, procedures, product information, or any other text you want your AI to learn from..."
                                            />
                                        </div>
                                        <div className="flex flex-col-reverse xs:flex-row justify-end gap-2 sm:gap-3">
                                            <button
                                                onClick={handleTextCancel}
                                                className="w-full xs:w-auto px-4 py-2 text-xs sm:text-sm bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors duration-200"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleTextSubmit}
                                                className="w-full xs:w-auto px-4 py-2 text-xs sm:text-sm bg-black dark:bg-white text-white dark:text-black rounded-full hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors duration-200"
                                            >
                                                Add Text
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Add Website Accordion */}
                        <div className="border border-gray-200 dark:border-white/[0.06] rounded-[16px] overflow-hidden">
                            <button
                                onClick={() => setActiveAccordion(activeAccordion === "website" ? null : "website")}
                                className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors duration-200"
                            >
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Add website</h2>
                                </div>
                                <ChevronDown
                                    className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ${
                                        activeAccordion === "website" ? "transform rotate-180" : ""
                                    }`}
                                />
                            </button>

                            <div
                                className={`transition-all duration-300 ease-in-out ${
                                    activeAccordion === "website" ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                                }`}
                            >
                                <div className="p-3 sm:p-4 border-t border-gray-100 dark:border-white/[0.06]">
                                    <div className="space-y-3 sm:space-y-4">
                                        <div>
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Website URL</label>
                                            <p className="text-xs text-gray-400 dark:text-gray-600 mb-2">
                                                Fetch any page from your website — homepage, pricing, about, FAQ, or any specific route. The extracted text will be added as a source for your AI.
                                            </p>
                                            <div className="flex gap-2">
                                                <input
                                                    type="url"
                                                    value={websiteForm.url}
                                                    onChange={(e) => setWebsiteForm({ url: e.target.value })}
                                                    onKeyDown={(e) => e.key === "Enter" && !scraping && handleWebsiteScrape()}
                                                    className="flex-1 px-3 py-2 rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-xs sm:text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/50"
                                                    placeholder={userWebsite || "https://example.com/pricing"}
                                                />
                                                <button
                                                    onClick={handleWebsiteScrape}
                                                    disabled={scraping || !websiteForm.url.trim()}
                                                    className="px-4 py-2 text-xs sm:text-sm bg-black dark:bg-white text-white dark:text-black rounded-full hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                                                >
                                                    {scraping ? (
                                                        <>
                                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                            Fetching...
                                                        </>
                                                    ) : (
                                                        "Fetch"
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Scraped content preview */}
                                        {scrapedData && (
                                            <div className="space-y-3">
                                                <div className="bg-gray-50 dark:bg-white/[0.03] rounded-[12px] p-3 sm:p-4">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{scrapedData.title}</h4>
                                                        <span className="text-xs text-gray-400 dark:text-gray-600 whitespace-nowrap ml-2">
                                                            {scrapedData.charCount.toLocaleString()} chars
                                                        </span>
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 max-h-40 overflow-y-auto whitespace-pre-wrap">
                                                        {scrapedData.content.length > 2000
                                                            ? `${scrapedData.content.substring(0, 2000)}...`
                                                            : scrapedData.content}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col-reverse xs:flex-row justify-end gap-2 sm:gap-3">
                                                    <button
                                                        onClick={handleWebsiteCancel}
                                                        className="w-full xs:w-auto px-4 py-2 text-xs sm:text-sm bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors duration-200"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleWebsiteSave}
                                                        className="w-full xs:w-auto px-4 py-2 text-xs sm:text-sm bg-black dark:bg-white text-white dark:text-black rounded-full hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors duration-200"
                                                    >
                                                        Add to Sources
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Cancel button when no scraped data */}
                                        {!scrapedData && (
                                            <div className="flex justify-end">
                                                <button
                                                    onClick={handleWebsiteCancel}
                                                    className="px-4 py-2 text-xs sm:text-sm bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors duration-200"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Added Q&A Items */}
                    {qaSourcesFromDB && qaSourcesFromDB.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Added Q&A ({qaSourcesFromDB.length})</h2>
                            <div className="space-y-3">
                                {qaSourcesFromDB.map((qa) => (
                                    <div
                                        key={qa.id}
                                        className="border border-gray-200 dark:border-white/[0.06] rounded-[12px] p-4 bg-white dark:bg-white/[0.02] cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 transition-colors duration-200"
                                        onClick={() => openModal("qa", qa)}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center space-x-2">
                                                <MessageSquare className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Q&A</span>
                                                <Eye className="w-4 h-4 text-gray-400" />
                                                <span className="text-xs text-gray-500">Click to view</span>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeFile(qa.id);
                                                }}
                                                className="text-gray-400 hover:text-red-600 transition-colors duration-200"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="space-y-2">
                                            <div>
                                                <span className="text-sm font-medium text-gray-900 dark:text-white">Q: </span>
                                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                                    {(qa.question || "").length > 100
                                                        ? `${(qa.question || "").substring(0, 100)}...`
                                                        : qa.question || ""}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-900 dark:text-white">A: </span>
                                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                                    {(qa.answer || "").length > 100 ? `${(qa.answer || "").substring(0, 100)}...` : qa.answer || ""}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                            Added {new Date(qa.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Added Text Items */}
                    {textSourcesFromDB && textSourcesFromDB.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Added Text Content ({textSourcesFromDB.length})</h2>
                            <div className="space-y-3">
                                {textSourcesFromDB.map((text) => (
                                    <div
                                        key={text.id}
                                        className="border border-gray-200 dark:border-white/[0.06] rounded-[12px] p-4 bg-white dark:bg-white/[0.02] cursor-pointer hover:border-green-300 dark:hover:border-green-600 transition-colors duration-200"
                                        onClick={() => openModal("text", text)}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center space-x-2">
                                                <Type className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                                <span className="text-sm font-medium text-green-600 dark:text-green-400">Text</span>
                                                <Eye className="w-4 h-4 text-gray-400" />
                                                <span className="text-xs text-gray-500">Click to view</span>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeFile(text.id);
                                                }}
                                                className="text-gray-400 hover:text-red-600 transition-colors duration-200"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="space-y-2">
                                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">{text.title || "Untitled"}</h3>
                                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                                {text.content && text.content.length > 150
                                                    ? `${text.content.substring(0, 150)}...`
                                                    : text.content || "No content available"}
                                            </p>
                                        </div>
                                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                            Added {new Date(text.created_at).toLocaleString()} • {(text.char_count || 0).toLocaleString()} characters
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* File Sources */}
                    <div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">File sources</h2>
                            <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2 sm:gap-4">
                                <div className="relative flex-1 xs:flex-initial xs:w-auto">
                                    <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2" />
                                    <input
                                        type="text"
                                        placeholder="Search files..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-8 sm:pl-9 pr-3 sm:pr-4 py-2 rounded-[12px] border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] text-xs sm:text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/30 focus:border-blue-500/50"
                                    />
                                </div>
                                <div className="flex items-center gap-2 relative" ref={sortDropdownRef}>
                                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 whitespace-nowrap">Sort by:</span>
                                    <button
                                        onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                                        className="flex items-center gap-1 text-xs sm:text-sm font-medium text-gray-900 dark:text-white hover:bg-black/[0.02] dark:hover:bg-white/[0.03] px-2 py-1 rounded-full transition-colors duration-200"
                                    >
                                        <span className="capitalize whitespace-nowrap">
                                            {sortBy === "name" ? "Name" : sortBy === "size" ? "Size" : sortBy === "oldest" ? "Oldest" : "Newest"}
                                        </span>
                                        <ChevronDown
                                            className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-200 flex-shrink-0 ${sortDropdownOpen ? "rotate-180" : ""}`}
                                        />
                                    </button>

                                    {sortDropdownOpen && (
                                        <div className="absolute top-full right-0 mt-2 w-32 bg-white dark:bg-[#111] border border-gray-200 dark:border-white/[0.06] rounded-[12px] shadow-xl z-10">
                                            {[
                                                { value: "newest", label: "Newest" },
                                                { value: "oldest", label: "Oldest" },
                                                { value: "name", label: "Name" },
                                                { value: "size", label: "Size" },
                                            ].map((option) => (
                                                <button
                                                    key={option.value}
                                                    onClick={() => {
                                                        setSortBy(option.value);
                                                        setSortDropdownOpen(false);
                                                    }}
                                                    className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-black/[0.02] dark:hover:bg-white/[0.03] first:rounded-t-[12px] last:rounded-b-[12px] ${
                                                        sortBy === option.value
                                                            ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                                                            : "text-gray-900 dark:text-white"
                                                    }`}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Sources Table */}
                        <div className="border border-gray-200 dark:border-white/[0.06] rounded-[16px] overflow-hidden">
                            <div className="p-4 bg-gray-50 dark:bg-white/[0.03] border-b border-gray-100 dark:border-white/[0.06]">
                                <div className="flex items-center justify-between">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={allVisibleSelected}
                                            ref={(el) => {
                                                if (el) {
                                                    el.indeterminate = someVisibleSelected && !allVisibleSelected;
                                                }
                                            }}
                                            onChange={toggleSelectAll}
                                            className="rounded border-gray-300 dark:border-gray-600 accent-blue-500"
                                        />
                                        <span className="text-sm text-gray-500 dark:text-gray-500">
                                            {selectedCount > 0 ? `${selectedCount} of ${visibleCount} selected` : "Select all"}
                                        </span>
                                    </label>
                                    {selectedCount > 0 && (
                                        <button
                                            onClick={deleteSelectedFiles}
                                            className="flex items-center space-x-1 px-3 py-1 text-sm bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors duration-200"
                                        >
                                            <X className="w-4 h-4" />
                                            <span>Delete selected ({selectedCount})</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="divide-y divide-gray-100 dark:divide-white/[0.06]">
                                {isLoadingSources ? (
                                    <InlineLoader text="Loading sources..." />
                                ) : filteredAndSortedSources.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-500 dark:text-gray-400 mb-2">
                                            {searchQuery ? "No files found matching your search." : "No files uploaded yet."}
                                        </p>
                                        {searchQuery && (
                                            <button onClick={() => setSearchQuery("")} className="text-blue-500 hover:text-blue-600 text-sm">
                                                Clear search
                                            </button>
                                        )}
                                        {!searchQuery && <p className="text-sm text-gray-400">Drag and drop files above to get started.</p>}
                                    </div>
                                ) : (
                                    (filteredAndSortedSources || []).map((source) => (
                                        <div key={source.id} className="flex items-center space-x-4 p-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.03]">
                                            <input
                                                type="checkbox"
                                                checked={isFileSelected(source.id)}
                                                onChange={() => toggleFileSelection(source.id)}
                                                className="rounded border-gray-300 dark:border-gray-600 accent-blue-500"
                                            />
                                            <div className="flex items-center space-x-3 flex-1">
                                                <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center space-x-2 mb-1">
                                                        <span className="font-medium text-gray-900 dark:text-white truncate">{source.name}</span>
                                                        {source.status === "New" && (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                                New
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-sm text-gray-500 dark:text-gray-500">{(source.char_count || 0).toLocaleString()} chars</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => removeFile(source.id)}
                                                    className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full text-gray-400 hover:text-red-600 transition-colors duration-200"
                                                    title="Delete file"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <LearnMoreModal isOpen={isLearnMoreOpen} onClose={() => setIsLearnMoreOpen(false)} />

            {/* Preview Modal */}
            {modal.show && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
                    onClick={closeModal}
                >
                    <div
                        className="bg-white dark:bg-[#111] rounded-[20px] border border-gray-200 dark:border-white/[0.06] shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-white/[0.06]">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {modal.type === "qa" ? "Q&A Preview" : "Text Content Preview"}
                            </h3>
                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            {modal.type === "qa" && modal.data && (
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Question:</h4>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-white/[0.03] p-3 rounded-[12px]">
                                            {modal.data.question}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Answer:</h4>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-white/[0.03] p-3 rounded-[12px] whitespace-pre-wrap">
                                            {modal.data.answer}
                                        </p>
                                    </div>
                                    <div className="pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Added {new Date(modal.data.created_at).toLocaleString()} • {(modal.data.char_count || 0).toLocaleString()}{" "}
                                            characters
                                        </p>
                                    </div>
                                </div>
                            )}

                            {modal.type === "text" && modal.data && (
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title:</h4>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-white/[0.03] p-3 rounded-[12px]">
                                            {modal.data.title || "Untitled"}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Content:</h4>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-white/[0.03] p-3 rounded-[12px] whitespace-pre-wrap">
                                            {modal.data.content || "No content available"}
                                        </p>
                                    </div>
                                    <div className="pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Added {new Date(modal.data.created_at).toLocaleString()} • {(modal.data.char_count || 0).toLocaleString()}{" "}
                                            characters
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end p-6 border-t border-gray-100 dark:border-white/[0.06]">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white rounded-full hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors duration-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toast.show && (
                <div className="fixed top-4 right-4 z-50">
                    <div
                        onClick={() => setToast({ show: false, message: "", type: "success" })}
                        className={`px-4 py-3 bg-white dark:bg-[#111] border border-gray-200 dark:border-white/[0.06] rounded-[12px] shadow-xl backdrop-blur-sm cursor-pointer hover:scale-105 text-gray-900 dark:text-white transform transition-all duration-200 ease-out animate-in slide-in-from-right-5`}
                    >
                        <div className="flex items-center space-x-2">
                            {toast.type === "success" ? (
                                <span className="text-green-600 dark:text-green-400 text-sm">✓</span>
                            ) : (
                                <span className="text-red-600 dark:text-red-400 text-sm">✗</span>
                            )}
                            <span className="text-sm font-medium">{toast.message}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Right Sticky Card - Sources Summary */}
            <div className="w-full lg:w-80 flex-shrink-0 lg:order-2 container-mobile lg:p-8 pb-6 sm:pb-8">
                <div className="lg:sticky lg:top-8">
                    {/* Card Content */}
                    <div className="bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] rounded-[16px] shadow-sm p-4 sm:p-6">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">Sources</h3>

                    <div className="space-y-4 sm:space-y-6">
                        {isLoadingSources ? (
                            <>
                                {/* Loading skeletons for categories */}
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 sm:gap-3">
                                            <SkeletonLoader className="w-5 h-5 sm:w-6 sm:h-6 rounded" />
                                            <div>
                                                <SkeletonLoader className="h-3 sm:h-4 w-16 sm:w-20 mb-1 sm:mb-2" />
                                                <SkeletonLoader className="h-2 sm:h-3 w-12 sm:w-16" />
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <SkeletonLoader className="h-4 sm:h-5 w-10 sm:w-12 mb-1" />
                                            <SkeletonLoader className="h-2 sm:h-3 w-6 sm:w-8" />
                                        </div>
                                    </div>
                                ))}
                            </>
                        ) : (
                            categories.map((category) => {
                                const charCount = parseInt(category.chars.split(" ")[0].replace(/,/g, ""));
                                return (
                                    <div key={category.id} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 sm:gap-3">
                                            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-100 dark:bg-white/[0.03] rounded flex items-center justify-center flex-shrink-0">
                                                {category.id === "files" && <FileText className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-600 dark:text-gray-400" />}
                                                {category.id === "text" && <Type className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-600 dark:text-gray-400" />}
                                                {category.id === "website" && <Globe className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-600 dark:text-gray-400" />}
                                                {category.id === "qa" && <MessageSquare className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-600 dark:text-gray-400" />}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                                                    {category.count} {category.label}
                                                </div>
                                                <div className="text-xs text-gray-400 dark:text-gray-600">{category.chars}</div>
                                            </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{charCount.toLocaleString()}</div>
                                            <div className="text-xs text-gray-400 dark:text-gray-600">chars</div>
                                        </div>
                                    </div>
                                );
                            })
                        )}

                        <div className="border-t border-gray-100 dark:border-white/[0.06] pt-4">
                            {isLoadingSources ? (
                                <>
                                    <div className="flex items-center justify-between mb-2">
                                        <SkeletonLoader className="h-4 w-20" />
                                        <SkeletonLoader className="h-4 w-32" />
                                    </div>
                                    <SkeletonLoader className="h-2 w-full rounded-full" />
                                </>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium text-gray-500 dark:text-gray-500 whitespace-nowrap">Characters</span>
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">{totalCharCount.toLocaleString()} / {maxCharactersTotal.toLocaleString()}</span>
                                    </div>
                                    <div className="mt-2 w-full bg-gray-200 dark:bg-white/[0.08] rounded-full h-2">
                                        <div
                                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${Math.min((totalCharCount / maxCharactersTotal) * 100, 100)}%` }}
                                        ></div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Update AI Agent section - commented out */}
                        {/* <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs">!</span>
              </div>
              <div>
                <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                  Changes will apply once you update the agent's context.
                </p>
              </div>
            </div>
            <button className="mt-3 w-full px-4 py-2 bg-orange-500 text-white rounded-md text-sm font-medium hover:bg-orange-600 transition-colors duration-200">
              Update AI Agent
            </button>
          </div> */}
                    </div>
                </div>
            </div>
            </div>

            {/* Upgrade Modal */}
            <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} feature="sources" />
        </div>
    );
};

export default SourcesPage;
